import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const glitch = keyframes`
  0% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  2% {
    clip-path: inset(10% 0 20% 0);
    transform: translate(-2px);
  }
  4% {
    clip-path: inset(40% 0 5% 0);
    transform: translate(2px);
  }
  6% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  50% { transform: translateY(100%); }
  100% { transform: translateY(-100%); }
`;

const typing = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const blink = keyframes`
  0%, 100% { border-color: transparent }
  50% { border-color: rgba(64, 128, 255, 0.8) }
`;

// Componentes estilizados
const QuestionContainer = styled.div`
  background: rgba(15, 25, 45, 0.6);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 10px;
  padding: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(64, 128, 255, 0.2);
    border-color: rgba(64, 128, 255, 0.5);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #4C6EF5, #64B6FF);
    border-radius: 10px 0 0 10px;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const QuestionText = styled.p`
  color: #fff;
  font-size: 1rem;
  margin: 0;
  padding-right: 2rem;
  font-weight: 500;
`;

const RemoveBtn = styled.button`
  background: rgba(255, 82, 82, 0.2);
  border: none;
  color: #FF5252;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  
  &:hover {
    background: rgba(255, 82, 82, 0.4);
    transform: scale(1.1);
  }
`;

const DisqualifyingSection = styled.div`
  margin-top: 1rem;
  position: relative;
`;

const SectionLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(100, 182, 255, 0.8);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    opacity: 0.8;
  }
`;

const DisqualifyingInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(64, 128, 255, 0.5);
  }
`;

const TechEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  border-radius: 10px;
  z-index: -1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(64, 128, 255, 0.1), transparent);
    animation: ${shimmer} 3s infinite;
  }
`;

const Scanline = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: rgba(64, 128, 255, 0.3);
  opacity: 0.5;
  z-index: 1;
  animation: ${scanline} 4s linear infinite;
  pointer-events: none;
`;

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#64B6FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const QualifyingQuestion = ({ question, onRemove, onUpdateDisqualifyingAnswer }) => {
  const [disqualifyingAnswer, setDisqualifyingAnswer] = useState(question.disqualifyingAnswer || '');

  const handleDisqualifyingAnswerChange = (e) => {
    const newAnswer = e.target.value;
    setDisqualifyingAnswer(newAnswer);
    onUpdateDisqualifyingAnswer(question.id, newAnswer);
  };

  return (
    <QuestionContainer>
      <TechEffect />
      <Scanline />
      <QuestionHeader>
        <QuestionText>{question.question}</QuestionText>
        <RemoveBtn onClick={() => onRemove(question.id)}>×</RemoveBtn>
      </QuestionHeader>
      
      <DisqualifyingSection>
        <SectionLabel>
          <InfoIcon /> Resposta Desqualificatória (opcional)
        </SectionLabel>
        <DisqualifyingInput
          type="text"
          placeholder="Digite uma resposta que desqualifica o lead (opcional)"
          value={disqualifyingAnswer}
          onChange={handleDisqualifyingAnswerChange}
        />
      </DisqualifyingSection>
    </QuestionContainer>
  );
};

export default QualifyingQuestion;
